import { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import HomePage from "./pages/HomePage";
import ResultsPage from "./pages/ResultsPage";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Methodology from "./pages/Methodology";
import AuditCrawlPage from "./pages/AuditCrawlPage";
import SharedScanPage from "./pages/SharedScanPage";
import NotFound from "./pages/NotFound";
import { trackPageView, isDebugMode } from "./lib/analytics";

const queryClient = new QueryClient();

// Route change tracker component
const RouteChangeTracker = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);

  return null;
};

// Debug mode indicator component
const DebugIndicator = () => {
  const [showDebug, setShowDebug] = useState(false);
  const location = useLocation();
  
  useEffect(() => {
    // Check debug mode - runs on mount and URL changes
    const checkDebug = () => {
      const debugActive = isDebugMode();
      setShowDebug(debugActive);
    };
    
    // Small delay to ensure window is ready
    const timer = setTimeout(checkDebug, 100);
    return () => clearTimeout(timer);
  }, [location.search]); // Re-check when URL params change
  
  if (!showDebug) return null;
  
  return (
    <div className="fixed bottom-4 right-4 bg-yellow-400 text-black px-3 py-1.5 rounded-full text-xs font-bold z-50 shadow-lg animate-pulse border-2 border-yellow-600">
      🐛 Debug Mode (Mock Data)
    </div>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <RouteChangeTracker />
          <DebugIndicator />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/results" element={<ResultsPage />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/methodology" element={<Methodology />} />
            <Route path="/audit-crawl" element={<AuditCrawlPage />} />
            <Route path="/scan/:scanId" element={<SharedScanPage />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
